import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useCustomerManagementStore } from "./store";
import { message, Modal, Typography } from "antd";
import { HOME_ROUTE, SIGN_IN_ROUTE } from "_constants";
import { useAuthUser } from "./useAuthUser";
import { CreateAccountConfirmModalContent } from "_lib";
import { getOnboardingStepRoute, ONBOARDING_BLOCKED } from "_helpers";
import { useNavigate } from "react-router-dom";
import { useMainStore } from "./store/useMainStore";

const messageKeyLoading = "googleSignInSignUpLoading";

export const useSocialSignIn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    customerManagement: { socialSignInStatus, socialSignInError, onboardingStatus, onboardingStatusDetails, user },
    clearSocialSignInStatus,
    socialSignIn,
    fetchOnboardingStatus,
    clearOnboardingStatus,
    logout,
  } = useCustomerManagementStore();

  const {
    main: { redirectPath },
    setOnboardingCheckPending,
  } = useMainStore();

  const { user: authUser } = useAuthUser();
  const { pathname } = useLocation();
  const [modal, contextHolder] = Modal.useModal();
  const [shouldRouteAfterSocialSignin, setShouldRouteAfterSocialSignin] = useState(false);
  const blockedModalOpenRef = useRef(false);

  const handleSocialSignIn = useCallback(() => {
    const code = searchParams.get("code");
    if (!code) return;
    socialSignIn({
      code,
      redirectUri: `${window.location.origin}${window.location.pathname}`,
    });
  }, [searchParams, socialSignIn]);

  useEffect(() => {
    handleSocialSignIn();
  }, [handleSocialSignIn]);

  const handleNotificationAndStatusClear = useCallback(() => {
    if (socialSignInStatus === "loading") {
      message.loading({
        content: "Authenticating.....",
        key: messageKeyLoading,
      });
    } else {
      message.destroy(messageKeyLoading);
    }
    if (socialSignInStatus === "error") {
      clearSocialSignInStatus();
      if (socialSignInError?.internalCode === "EMPIREFX-USER-063") {
        navigate(pathname, { replace: true });
        let modalRef = null;
        modalRef = modal.confirm({
          title: "Create new account",
          centered: true,
          content: <CreateAccountConfirmModalContent message={socialSignInError?.message} onClose={() => modalRef?.destroy()} />,
          okButtonProps: { style: { display: "none" } },
          cancelButtonProps: { style: { display: "none" } },
        });
        return;
      }
      message.error("Authentication failed", 2);
    }
  }, [clearSocialSignInStatus, modal, navigate, pathname, socialSignInError, socialSignInStatus]);

  useEffect(() => {
    handleNotificationAndStatusClear();
  }, [handleNotificationAndStatusClear]);

  // Trigger onboarding status fetch after successful social sign-in
  useEffect(() => {
    if (socialSignInStatus === "success" && pathname === SIGN_IN_ROUTE) {
      message.destroy(messageKeyLoading);
      message.success("Authentication successful", 2);
      setOnboardingCheckPending(true);
      clearSocialSignInStatus();
      setShouldRouteAfterSocialSignin(true);
      fetchOnboardingStatus();
    }
  }, [clearSocialSignInStatus, fetchOnboardingStatus, pathname, setOnboardingCheckPending, socialSignInStatus]);

  // Route to correct onboarding step once status is fetched
  useEffect(() => {
    if (!shouldRouteAfterSocialSignin) return;

    if (onboardingStatus === "error") {
      setOnboardingCheckPending(false);
      clearOnboardingStatus();
      setShouldRouteAfterSocialSignin(false);
      navigate(redirectPath || HOME_ROUTE, { replace: true });
      return;
    }

    if (onboardingStatus !== "success") return;

    setOnboardingCheckPending(false);
    clearOnboardingStatus();
    setShouldRouteAfterSocialSignin(false);

    const onboardingRoute = getOnboardingStepRoute(onboardingStatusDetails);

    if (onboardingRoute === ONBOARDING_BLOCKED) {
      if (blockedModalOpenRef.current) return;
      blockedModalOpenRef.current = true;
      const userEmail = user?.email || onboardingStatusDetails?.email || "";
      const handleClose = () => {
        blockedModalOpenRef.current = false;
        setOnboardingCheckPending(false);
        logout();
      };
      Modal.error({
        title: "Account Blocked",
        content: (
          <Typography.Text>
            {userEmail && (
              <>
                The account <Typography.Text strong>{userEmail}</Typography.Text> has been blocked.
              </>
            )}
            {!userEmail && "Your account has been blocked."}
            {" "}Please contact our support team for assistance.
            <br /><br />
            <Typography.Text strong>Phone: </Typography.Text>
            <Typography.Link href="tel:+254793333333">+254 793 333 333</Typography.Link>
            <br />
            <Typography.Text strong>Email: </Typography.Text>
            <Typography.Link href="mailto:support@empirefx.com">support@empirefx.com</Typography.Link>
          </Typography.Text>
        ),
        okText: "OK",
        centered: true,
        onOk: handleClose,
        onCancel: handleClose,
      });
      return;
    }

    // New user via Google — show create account confirm
    if (authUser?.isSignup) {
      modal.confirm({
        title: "Create new account",
        centered: true,
        content: <CreateAccountConfirmModalContent />,
        okText: "Proceed",
        cancelText: "Cancel",
        okButtonProps: { style: { display: "none" } },
        cancelButtonProps: { style: { display: "none" } },
      });
      return;
    }

    const destination =
      onboardingRoute === HOME_ROUTE ? redirectPath || HOME_ROUTE : onboardingRoute;
    navigate(destination, { replace: true });
  }, [
    authUser?.isSignup,
    clearOnboardingStatus,
    logout,
    modal,
    navigate,
    onboardingStatus,
    onboardingStatusDetails,
    redirectPath,
    setOnboardingCheckPending,
    shouldRouteAfterSocialSignin,
    user?.email,
  ]);

  return { socialSignInModalContextHolder: contextHolder };
};
